import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeftIcon,
  UsersIcon,
  UserCheckIcon,
  ShieldCheckIcon,
  ShieldAlertIcon,
  CheckIcon,
  BanIcon,
  LoaderIcon,
  Building2Icon,
  ExternalLinkIcon,
  LayersIcon,
  AlertCircleIcon,
  Settings2Icon,
  ClockIcon,
  QrCodeIcon,
  CopyIcon,
  XIcon
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { thingsboardService } from '../../services/thingsboard';
import { Zone } from '../../types';

interface UserManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  spaceName?: string;
  zones: Zone[];
}

export function UserManagementModal({
  isOpen,
  onClose,
  customerId,
  spaceName = '공간',
  zones
}: UserManagementModalProps) {
  const [activeTab, setActiveTab] = useState<'pending' | 'active' | 'qrcode'>('pending');
  const [users, setUsers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [copiedType, setCopiedType] = useState<'uuid' | 'url' | null>(null);

  // Approval / Edit Permission Modal State
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<any | null>(null);
  const [editRole, setEditRole] = useState<'CUSTOMER_ADMIN' | 'ZONE_USER'>('ZONE_USER');
  const [editAllowedZoneIds, setEditAllowedZoneIds] = useState<string[]>([]);
  const [editCanControl, setEditCanControl] = useState(true);
  const [editError, setEditError] = useState('');

  // Load Customer Users
  const loadUsers = async () => {
    if (!customerId) return;
    try {
      setIsLoading(true);
      const list = await thingsboardService.getCustomerUsersWithAttributes(customerId);
      setUsers(list);
    } catch (err) {
      console.error('Failed to load customer users', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
  }, [isOpen, customerId]);

  if (!isOpen) return null;

  const pendingUsers = users.filter((u) => u.status === 'PENDING_APPROVAL');
  const activeUsers = users.filter((u) => u.status !== 'PENDING_APPROVAL');

  // Open Permission Assignment Sub-modal
  const handleOpenEditModal = (user: any) => {
    setSelectedUserForEdit(user);
    setEditRole(user.userRole === 'CUSTOMER_ADMIN' ? 'CUSTOMER_ADMIN' : 'ZONE_USER');
    setEditAllowedZoneIds(Array.isArray(user.allowedZoneIds) ? user.allowedZoneIds : []);
    setEditCanControl(user.canControl !== undefined ? user.canControl : true);
    setEditError('');
  };

  // Toggle Zone selection
  const handleToggleZone = (zoneId: string) => {
    setEditAllowedZoneIds((prev) =>
      prev.includes(zoneId) ? prev.filter((id) => id !== zoneId) : [...prev, zoneId]
    );
  };

  // Select all zones
  const handleSelectAllZones = () => {
    if (editAllowedZoneIds.length === zones.length) {
      setEditAllowedZoneIds([]);
    } else {
      setEditAllowedZoneIds(zones.map((z) => z.id));
    }
  };

  // Submit Approval / Permission Update
  const handleSavePermission = async () => {
    if (!selectedUserForEdit) return;
    setEditError('');

    if (editRole === 'ZONE_USER' && editAllowedZoneIds.length === 0) {
      setEditError('일반 사용자에게 최소 1개 이상의 접근 허용 구역(Zone)을 선택해주세요.');
      return;
    }

    try {
      setActionLoadingId(selectedUserForEdit.id.id);
      await thingsboardService.approveCustomerUser(selectedUserForEdit.id.id, {
        allowedZoneIds: editRole === 'CUSTOMER_ADMIN' ? zones.map((z) => z.id) : editAllowedZoneIds,
        userRole: editRole,
        canControl: editCanControl
      });

      setSelectedUserForEdit(null);
      await loadUsers();
    } catch (err: any) {
      setEditError(err.message || '권한 저장 중 오류가 발생했습니다.');
    } finally {
      setActionLoadingId(null);
    }
  };

  // Reject User
  const handleReject = async (userId: string) => {
    if (!confirm('정말 이 사용자의 가입을 거절하시겠습니까?')) return;
    try {
      setActionLoadingId(userId);
      await thingsboardService.rejectCustomerUser(userId);
      await loadUsers();
    } catch (err) {
      console.error('Failed to reject user', err);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleCopy = (text: string, type: 'uuid' | 'url') => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopiedType(type);
      setTimeout(() => setCopiedType(null), 2000);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[500] flex justify-center select-none"
      style={{ backgroundColor: 'var(--mobile-bg, #0b111e)' }}
    >
      <div
        className="w-full max-w-md h-[100dvh] max-h-[100dvh] flex flex-col shadow-2xl relative overflow-hidden"
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)',
          backgroundColor: 'var(--mobile-bg, #0b111e)',
          color: 'var(--mobile-text-primary, #ffffff)'
        }}
      >
        {/* ─── Top Header Bar (Standard Modal Style) ─── */}
        <div
          className="h-14 flex items-center justify-between px-4 border-b flex-shrink-0 z-10"
          style={{ borderColor: 'var(--mobile-border)', backgroundColor: 'var(--mobile-bg-header)' }}
        >
          <button
            onClick={onClose}
            className="p-2 -ml-2 rounded-lg text-slate-400 hover:text-white active:scale-95 transition-colors flex items-center gap-1"
            title="닫기"
          >
            <ChevronLeftIcon className="w-6 h-6 text-slate-300" />
            <span className="text-xs font-medium text-slate-300">닫기</span>
          </button>
          <h2 className="text-base font-bold text-cyan-400">
            사용자 및 권한 관리
          </h2>
          <div className="px-2.5 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-semibold">
            {spaceName}
          </div>
        </div>

        {/* ─── Tab Navigation Bar ─── */}
        <div className="flex border-b border-white/10 bg-slate-950/40 px-3 pt-2.5 gap-1 overflow-x-auto no-scrollbar flex-shrink-0">
          <button
            onClick={() => setActiveTab('pending')}
            className={`pb-2.5 px-3 text-xs font-bold transition-all relative flex items-center gap-1.5 whitespace-nowrap ${
              activeTab === 'pending'
                ? 'text-cyan-400 border-b-2 border-cyan-400'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <ClockIcon className="w-3.5 h-3.5" />
            <span>가입 승인 대기</span>
            {pendingUsers.length > 0 && (
              <span className="px-1.5 py-0.2 rounded-full bg-rose-500 text-white text-[0.65rem] font-bold">
                {pendingUsers.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('active')}
            className={`pb-2.5 px-3 text-xs font-bold transition-all relative flex items-center gap-1.5 whitespace-nowrap ${
              activeTab === 'active'
                ? 'text-cyan-400 border-b-2 border-cyan-400'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <UserCheckIcon className="w-3.5 h-3.5" />
            <span>등록된 사용자</span>
            <span className="px-1.5 py-0.2 rounded-full bg-white/10 text-slate-300 text-[0.65rem]">
              {activeUsers.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('qrcode')}
            className={`pb-2.5 px-3 text-xs font-bold transition-all relative flex items-center gap-1.5 whitespace-nowrap ${
              activeTab === 'qrcode'
                ? 'text-cyan-400 border-b-2 border-cyan-400'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <QrCodeIcon className="w-3.5 h-3.5" />
            <span>공간 정보 QR 코드</span>
          </button>
        </div>

        {/* ─── Scrollable Body Content ─── */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 pb-24 overscroll-contain" style={{ WebkitOverflowScrolling: "touch" }}>
          {isLoading ? (
            <div className="py-16 flex flex-col items-center justify-center gap-3 text-slate-500 text-xs">
              <LoaderIcon className="w-6 h-6 animate-spin text-cyan-400" />
              <span>사용자 목록을 불러오는 중...</span>
            </div>
          ) : activeTab === 'pending' ? (
            /* 1. Pending List */
            pendingUsers.length === 0 ? (
              <div className="py-16 text-center text-slate-500 text-xs space-y-1">
                <CheckIcon className="w-8 h-8 mx-auto text-emerald-500/50 mb-2" />
                <p className="font-semibold text-slate-400">승인 대기 중인 신규 가입자가 없습니다.</p>
                <p className="text-[0.7rem]">새로운 사용자가 회원가입 신청 시 이곳에 표시됩니다.</p>
              </div>
            ) : (
              pendingUsers.map((u) => (
                <div
                  key={u.id.id}
                  className="p-4 rounded-2xl border border-amber-500/20 bg-amber-500/5 flex items-center justify-between gap-3 shadow-sm"
                >
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-xs text-white truncate">{u.firstName || u.email}</span>
                      <span className="text-[0.68rem] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 font-semibold">
                        승인 대기
                      </span>
                    </div>
                    <div className="text-[0.72rem] text-slate-400 truncate">{u.email}</div>
                    {u.additionalInfo?.customerTitle && (
                      <div className="text-[0.68rem] text-cyan-400 flex items-center gap-1">
                        <Building2Icon className="w-3 h-3" />
                        <span>{u.additionalInfo.customerTitle}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => handleOpenEditModal(u)}
                      disabled={actionLoadingId === u.id.id}
                      className="px-3 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-xs flex items-center gap-1 transition-all shadow-md shadow-cyan-500/20 active:scale-95"
                    >
                      <ShieldCheckIcon className="w-3.5 h-3.5" />
                      <span>사용 승인</span>
                    </button>
                    <button
                      onClick={() => handleReject(u.id.id)}
                      disabled={actionLoadingId === u.id.id}
                      className="p-2 rounded-xl bg-white/5 hover:bg-rose-500/20 text-slate-400 hover:text-rose-400 transition-colors"
                      title="가입 거절"
                    >
                      <BanIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))
            )
          ) : activeTab === 'active' ? (
            /* 2. Active List */
            activeUsers.length === 0 ? (
              <div className="py-16 text-center text-slate-500 text-xs">
                등록된 사용자가 없습니다.
              </div>
            ) : (
              activeUsers.map((u) => {
                const isAdmin = u.userRole === 'CUSTOMER_ADMIN';
                const isRejected = u.status === 'REJECTED';
                const userZoneNames = zones
                  .filter((z) => (u.allowedZoneIds || []).includes(z.id))
                  .map((z) => z.name);

                return (
                  <div
                    key={u.id.id}
                    className="p-4 rounded-2xl border border-white/5 bg-slate-950/40 flex items-center justify-between gap-3 shadow-sm"
                  >
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-xs text-white truncate">{u.firstName || u.email}</span>
                        {isAdmin ? (
                          <span className="text-[0.68rem] px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20 font-bold">
                            전체 관리자
                          </span>
                        ) : isRejected ? (
                          <span className="text-[0.68rem] px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/20 font-semibold">
                            가입 거절됨
                          </span>
                        ) : (
                          <span className="text-[0.68rem] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-semibold">
                            일반 사용자
                          </span>
                        )}
                      </div>
                      <div className="text-[0.72rem] text-slate-400 truncate">{u.email}</div>
                      {!isAdmin && !isRejected && (
                        <div className="text-[0.68rem] text-slate-400 flex items-center gap-1 flex-wrap">
                          <LayersIcon className="w-3 h-3 text-cyan-400 flex-shrink-0" />
                          <span>허용 공간:</span>
                          {userZoneNames.length > 0 ? (
                            <span className="text-cyan-300 font-medium">
                              {userZoneNames.join(', ')}
                            </span>
                          ) : (
                            <span className="text-rose-400">지정된 공간 없음</span>
                          )}
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => handleOpenEditModal(u)}
                      className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white font-semibold text-xs flex items-center gap-1 transition-colors flex-shrink-0"
                    >
                      <Settings2Icon className="w-3.5 h-3.5" />
                      <span>권한 수정</span>
                    </button>
                  </div>
                );
              })
            )
          ) : (
            /* 3. Space QR Code Tab */
            (() => {
              const signUpUrl = typeof window !== 'undefined' && window.location?.origin
                ? `${window.location.origin}/?customer=${customerId}&signup=true`
                : `https://sedb.seqsys.net/?customer=${customerId}&signup=true`;

              return (
                <div className="py-2 flex flex-col items-center text-center space-y-4">
                  <div className="space-y-1">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-semibold">
                      <Building2Icon className="w-3.5 h-3.5" />
                      <span>{spaceName} 전용 회원가입 QR</span>
                    </div>
                    <p className="text-xs text-slate-400 pt-1">
                      카메라로 스캔하거나 QR 카드를 클릭하면 이 공간의 코드가 자동 입력되어 바로 회원가입이 진행됩니다.
                    </p>
                  </div>

                  {/* QR Code Card (Clickable) */}
                  <div
                    onClick={() => window.open(signUpUrl, '_blank')}
                    className="p-5 rounded-3xl bg-white shadow-2xl shadow-cyan-500/10 border-4 border-cyan-500/30 flex flex-col items-center justify-center transition-all hover:scale-105 duration-300 cursor-pointer group hover:border-cyan-400 relative active:scale-95"
                    title="클릭 시 회원가입 페이지 새 창으로 열기"
                  >
                    <QRCodeSVG
                      value={signUpUrl}
                      size={180}
                      level="H"
                      includeMargin={false}
                    />
                    <div className="mt-3 text-[0.75rem] font-bold text-slate-800 tracking-wider flex items-center gap-1.5 group-hover:text-cyan-600 transition-colors">
                      <span>SED · {spaceName}</span>
                      <ExternalLinkIcon className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100" />
                    </div>
                    <div className="mt-1 text-[0.68rem] font-medium text-cyan-600/90 group-hover:text-cyan-700 underline">
                      클릭하여 가입 화면 열기 ↗
                    </div>
                  </div>

                  {/* Space & UUID Info Box */}
                  <div className="w-full rounded-2xl bg-slate-900/80 border border-white/10 p-4 text-left space-y-2.5 shadow-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-[0.7rem] font-semibold text-slate-400">공간 이름</span>
                      <span className="text-xs font-bold text-cyan-400">{spaceName}</span>
                    </div>

                    <div className="border-t border-white/5 pt-2 space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[0.7rem] font-semibold text-slate-400">Customer UUID (공간 코드)</span>
                        <button
                          onClick={() => handleCopy(customerId, 'uuid')}
                          className="flex items-center gap-1 text-[0.7rem] text-cyan-400 hover:text-cyan-300 font-medium px-2 py-0.5 rounded-md bg-cyan-500/10 transition-colors"
                        >
                          {copiedType === 'uuid' ? (
                            <>
                              <CheckIcon className="w-3 h-3 text-emerald-400" />
                              <span className="text-emerald-400 font-bold">복사됨!</span>
                            </>
                          ) : (
                            <>
                              <CopyIcon className="w-3 h-3" />
                              <span>복사</span>
                            </>
                          )}
                        </button>
                      </div>
                      <div className="p-2 rounded-lg bg-black/40 border border-white/5 font-mono text-[0.7rem] text-slate-300 break-all select-all">
                        {customerId || '배정된 UUID 없음'}
                      </div>
                    </div>
                  </div>

                  {/* Copy Link Button */}
                  <div className="w-full pt-1">
                    <button
                      onClick={() => handleCopy(signUpUrl, 'url')}
                      className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold text-xs shadow-lg shadow-cyan-500/20 flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                    >
                      {copiedType === 'url' ? (
                        <>
                          <CheckIcon className="w-4 h-4 text-white" />
                          <span>가입 링크가 복사되었습니다!</span>
                        </>
                      ) : (
                        <>
                          <CopyIcon className="w-4 h-4" />
                          <span>회원가입 링크 복사하기</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })()
          )}
        </div>

        {/* ─── Sub-modal: Assign Role & Zones ─── */}
        <AnimatePresence>
          {selectedUserForEdit && (
            <div className="absolute inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 15 }}
                className="w-full max-w-sm rounded-3xl border border-white/15 bg-slate-900 p-5 space-y-4 shadow-2xl"
              >
                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                  <div>
                    <h3 className="text-sm font-bold text-white">사용자 권한 지정</h3>
                    <p className="text-[0.72rem] text-slate-400">{selectedUserForEdit.email}</p>
                  </div>
                  <button
                    onClick={() => setSelectedUserForEdit(null)}
                    className="text-slate-400 hover:text-white"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>

                {editError && (
                  <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs flex items-center gap-2">
                    <AlertCircleIcon className="w-4 h-4 text-rose-400 flex-shrink-0" />
                    <span>{editError}</span>
                  </div>
                )}

                {/* 1. Role Selection */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-300">사용자 역할</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setEditRole('ZONE_USER')}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        editRole === 'ZONE_USER'
                          ? 'border-cyan-400 bg-cyan-500/15 text-white ring-1 ring-cyan-400'
                          : 'border-white/10 bg-slate-950/40 text-slate-400 hover:border-white/20'
                      }`}
                    >
                      <div className="font-bold text-xs text-cyan-300">일반 사용자</div>
                      <div className="text-[0.65rem] text-slate-400 mt-0.5">선택된 특정 공간만 제어</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setEditRole('CUSTOMER_ADMIN')}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        editRole === 'CUSTOMER_ADMIN'
                          ? 'border-purple-400 bg-purple-500/15 text-white ring-1 ring-purple-400'
                          : 'border-white/10 bg-slate-950/40 text-slate-400 hover:border-white/20'
                      }`}
                    >
                      <div className="font-bold text-xs text-purple-300">전체 관리자</div>
                      <div className="text-[0.65rem] text-slate-400 mt-0.5">모든 구역 및 회원 관리</div>
                    </button>
                  </div>
                </div>

                {/* 2. Zone Selection (Visible when ZONE_USER) */}
                {editRole === 'ZONE_USER' && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-semibold text-slate-300 flex items-center gap-1">
                        <LayersIcon className="w-3.5 h-3.5 text-cyan-400" />
                        <span>접근 및 제어 허용 공간 (Zone)</span>
                      </label>
                      <button
                        type="button"
                        onClick={handleSelectAllZones}
                        className="text-[0.68rem] text-cyan-400 hover:underline"
                      >
                        {editAllowedZoneIds.length === zones.length ? '전체 해제' : '전체 선택'}
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-1">
                      {zones.map((zone) => {
                        const isChecked = editAllowedZoneIds.includes(zone.id);
                        return (
                          <div
                            key={zone.id}
                            onClick={() => handleToggleZone(zone.id)}
                            className={`p-2.5 rounded-xl border cursor-pointer flex items-center gap-2 transition-all ${
                              isChecked
                                ? 'border-cyan-400 bg-cyan-500/15 text-cyan-200'
                                : 'border-white/5 bg-slate-950/40 text-slate-400 hover:border-white/10'
                            }`}
                          >
                            <div
                              className={`w-4 h-4 rounded flex items-center justify-center border flex-shrink-0 ${
                                isChecked ? 'bg-cyan-500 border-cyan-400 text-slate-950' : 'border-white/20'
                              }`}
                            >
                              {isChecked && <CheckIcon className="w-3 h-3 stroke-[3]" />}
                            </div>
                            <span className="text-xs font-medium truncate">{zone.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Buttons */}
                <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
                  <button
                    type="button"
                    onClick={() => setSelectedUserForEdit(null)}
                    className="px-4 py-2 rounded-xl text-xs text-slate-400 hover:text-white transition-colors"
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    onClick={handleSavePermission}
                    disabled={actionLoadingId !== null}
                    className="px-5 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-xs transition-all shadow-md shadow-cyan-500/20 active:scale-95 flex items-center gap-1.5"
                  >
                    {actionLoadingId ? (
                      <>
                        <LoaderIcon className="w-3.5 h-3.5 animate-spin" />
                        <span>저장 중...</span>
                      </>
                    ) : (
                      <>
                        <CheckIcon className="w-3.5 h-3.5" />
                        <span>권한 저장</span>
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
